import React, { useMemo } from 'react';
import { Horizontal, Vertical } from 'packages/radiant-react/layout';
import { Modal, ModalSize, ModalType } from 'packages/radiant-react/modal';
import { TextArea } from 'packages/radiant-react/text-area';
import { Button } from 'packages/radiant-react/button';
import { Typography, Colors } from 'packages/radiant-react/typography';
import styles from './styles.module.scss';
import { Icon, IconColor, IconID, IconSize } from 'packages/radiant-react/icon';

export interface CodeModalProps {
  isVisible: boolean;
  closeModal: () => void;
  headerProps?: {
    title?: string;
    heading?: string;
    tooltip?: string;
    onClick?: () => void;
  };
  confirmButtonProps?: {
    title?: string;
    onClick?: () => void;
  };
  dismissButtonProps?: {
    title: string;
    onClick: () => void;
  };
  content: string;
  title: string;
  textAreaProps?: {
    placeholder: string;
    rows: number;
    readOnly: boolean;
    onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  };
  errorProps?: {
    isError: boolean;
    errorMessage: string;
  };
  codeType?: string;
}

export const CodeModal = React.memo(
  ({
    isVisible,
    closeModal,
    headerProps,
    confirmButtonProps,
    dismissButtonProps,
    content,
    title,
    textAreaProps,
    errorProps,
    codeType,
  }: CodeModalProps) => {
    let isEmpty = false;
    try {
      if (!content || content.trim() === '') {
        isEmpty = true;
      } else {
        const parsed = JSON.parse(content);
        isEmpty = Object.keys(parsed).length === 0;
      }
    } catch (e) {
      isEmpty = true;
    }

    const confirmButton = useMemo(
      () => ({
        text: confirmButtonProps?.title || '',
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onConfirm: confirmButtonProps?.onClick || (() => {}),
        isDisabled: errorProps?.isError || isEmpty,
      }),
      [confirmButtonProps?.title, confirmButtonProps?.onClick, isEmpty, errorProps?.isError]
    );

    const dismissButton = useMemo(
      () => ({
        text: dismissButtonProps?.title || '',
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onClick: dismissButtonProps?.onClick || (() => {}),
      }),
      [dismissButtonProps?.title, dismissButtonProps?.onClick]
    );

    return (
      <Modal
        isOpen={isVisible}
        isContentLoading={false}
        size={ModalSize.MEDIUM}
        title={title}
        onDismiss={closeModal}
        confirmButton={confirmButton}
        dismissButton={dismissButton}
      >
        <Vertical className={styles.modalContent}>
          <Horizontal className={styles.modalHeader}>
            <Typography variant="p-modal-description" color={Colors.base}>
              {headerProps?.heading}
            </Typography>
            <Button
              text={headerProps?.title}
              // eslint-disable-next-line @typescript-eslint/no-empty-function
              onClick={headerProps?.onClick || (() => {})}
              type="secondary"
              size="s"
              isFullWidth={true}
              tooltip={headerProps?.tooltip}
              className={styles.copyButton}
              isDisabled={isEmpty}
            />
          </Horizontal>
          {errorProps?.isError ? (
            <Horizontal className={styles.errorContainer}>
              <Icon
                id={'exclamation-point-circle' as IconID}
                color={IconColor.RED}
                size={IconSize.XSMALL}
              />
              <Typography variant="p-modal-error" color={Colors.failure}>
                {errorProps.errorMessage}
              </Typography>
            </Horizontal>
          ) : (
            <Typography variant="p" color={Colors.base}>
              {codeType}
            </Typography>
          )}
          <Vertical>
            <TextArea
              placeholder={textAreaProps?.placeholder}
              value={content}
              className={styles.modalTextArea}
              rows={textAreaProps?.rows}
              readOnly={textAreaProps?.readOnly}
              onChange={textAreaProps?.onChange}
              hasError={errorProps?.isError}
              // textAreaClassName={styles.modalTextAreaClass}
            />
          </Vertical>
        </Vertical>
      </Modal>
    );
  }
);

CodeModal.displayName = 'CodeModal';
